import { useEffect } from 'react';
import { deferSwReload, shouldDeferSwReload } from '@/lib/swReloadGuard';

/**
 * Offline sync worker — démarre la queue qui replay les mutations Supabase
 * quand le réseau revient. Doit tourner une seule fois pour toute la session.
 */
export function OfflineSyncBootstrap() {
  useEffect(() => {
    // Lazy-import pour éviter de charger le service si pas utilisé immédiatement
    import('@/services/offlineSyncQueue')
      .then(({ startSyncWorker }) => {
        startSyncWorker();
      })
      .catch((err) => console.warn('[offline-sync] bootstrap failed:', err));
  }, []);
  return null;
}

/**
 * PWA update prompt — silently applies SW updates without blocking the UI.
 * No more full-screen spinner or repeating toast — the autoUpdate + skipWaiting
 * config in vite-plugin-pwa handles everything. This component just ensures
 * a clean reload happens once (with anti-loop guard).
 */
export function PwaUpdatePrompt() {
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // Anti-loop: if we reloaded for an update less than 15s ago, don't reload again
    // Use localStorage (sessionStorage unreliable in Android WebView)
    const RELOAD_KEY = 'vp_sw_reload_ts';
    const lastReload = Number(localStorage.getItem(RELOAD_KEY) || '0');
    const now = Date.now();
    if (now - lastReload < 15000) return;

    let refreshing = false;
    const reload = () => {
      if (refreshing) return;
      refreshing = true;
      localStorage.setItem(RELOAD_KEY, String(Date.now()));
      window.location.reload();
    };
    const handleControllerChange = () => {
      if (shouldDeferSwReload(window.location.pathname)) deferSwReload(reload);
      else reload();
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    // If a SW is already waiting, activate it silently
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg) return;
      if (reg.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        return;
      }
      reg.addEventListener('updatefound', () => {
        const newSW = reg.installing;
        if (!newSW) return;
        newSW.addEventListener('statechange', () => {
          if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
            newSW.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      });
    });

    return () =>
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
  }, []);

  // No visible UI — updates are silent
  return null;
}
